import { useEffect, useState, useRef } from 'react';
import { DEFAULT_LANGUAGE, DEFAULT_LOCALE, DEFAULT_TIMEZONE } from '../constants/GeneralUserInfo';

const resolveDefaultLocale = (locales, language) => {
    if (language === DEFAULT_LANGUAGE) {
        return DEFAULT_LOCALE;
    }
    return Object.keys(locales)?.[0];
};

const hasLocale = (locales = {}, userLocale) =>
    Object.keys(locales).some((locale) => locale === userLocale);

const getLocalizedUserInfo = (userInfo, locales) => {
    const { language, locale, timezone } = userInfo;

    return {
        language: language ?? DEFAULT_LANGUAGE,
        timezone: timezone ?? DEFAULT_TIMEZONE,
        locale: hasLocale(locales, locale)
            ? locale
            : resolveDefaultLocale(locales, language, locale),
    };
};

/**
 * Manage state for users language, locale and timezone
 * - initial values are set in useEffect
 * - user modified values are set with state setters
 *
 * @param {NotificationCookie} notificationCookie
 * @param {object} locales i.e. { en_US: 'United States', en_AU: 'Australia', en_CA: 'Canada' }
 * @param {boolean} shouldDecoupleLanguageLocale
 */
const useLanguageLocaleTimezoneValues = (
    notificationCookie,
    locales,
    shouldDecoupleLanguageLocale,
) => {
    // simplified state for when feature flag is on
    // - move these three to RegisterPage when feature flag is removed
    // - delete this entire hook - no longer need to manage the complexity language and locale changes
    const [language, setLanguage] = useState(notificationCookie?.language ?? DEFAULT_LANGUAGE);
    const [locale, setLocale] = useState(notificationCookie?.locale ?? DEFAULT_LOCALE);
    const [timezone, setTimezone] = useState(notificationCookie?.timezone ?? DEFAULT_TIMEZONE);

    const [initialLanguage, setInitialLanguage] = useState(
        notificationCookie?.language ?? DEFAULT_LANGUAGE,
    );

    const [userLanguage, setUserLanguage] = useState(initialLanguage);
    const [userLocale, setUserLocale] = useState(locale ?? resolveDefaultLocale(locales, language));
    const [userTimezone, setUserTimezone] = useState(timezone);
    const clearedLocale = useRef(false);

    const handleLocaleChange = (loc) => {
        if (!loc) {
            // LanguageField clears the locale when a new language is selected
            // - this prevents auto generating a new locale
            clearedLocale.current = true;
        }
        setUserLocale(loc);
    };

    useEffect(() => {
        const defaultLocalizationValues = getLocalizedUserInfo(
            { language, locale, timezone: userTimezone },
            locales,
        );
        setInitialLanguage(defaultLocalizationValues.language);
        setUserTimezone(defaultLocalizationValues.timezone);
        if (!clearedLocale.current) {
            setUserLocale(defaultLocalizationValues.locale);
        }
        // notificationCookie values are used only for the initial render
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [locales]);

    if (shouldDecoupleLanguageLocale) {
        return {
            language,
            setLanguage,
            locale,
            setLocale,
            timezone,
            setTimezone,
        };
    }

    return {
        language: userLanguage,
        setLanguage: setUserLanguage,
        locale: userLocale,
        setLocale: handleLocaleChange,
        timezone: userTimezone,
        setTimezone: setUserTimezone,
    };
};

export default useLanguageLocaleTimezoneValues;
